import { filterNullOrUndefined, filterUndefined, HydraCollection, User } from '@adeo/ngx-kozikaza-api';
import { Product, ProductService, UserKazaplanServiceProduct, UserKazaplanServiceProductService } from '@adeo/ngx-kozikaza-api/payment';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, retry, switchMap, throwError } from 'rxjs';
import { catchError, filter, map, take, takeWhile, tap } from 'rxjs/operators';
import { distinctObject } from '../../modules/auth/auth.service';
import { UserStoreService } from '../../shared/services/state-management/user-store.service';

@Injectable()
export class KozikashService {
  public isProductsLoading = new BehaviorSubject(false);

  private products: Array<Product>;

  private productsCache = {};

  constructor(
    private readonly userStoreService: UserStoreService,
    private readonly userKazaplanServiceProductService: UserKazaplanServiceProductService,
    private readonly productService: ProductService,
  ) {
  }


  lookForPremiumProduct(): Observable<UserKazaplanServiceProduct> {
    return this.userStoreService.userStore
      .pipe(
        catchError((e) => {
          console.log('lookForPurchasedProduct / catchError', e);

          return throwError(() => e);
        }),
        filterNullOrUndefined(),
        distinctObject(),
        take(1),
        switchMap((user: User) => !user ? of(null) : this.userKazaplanServiceProductService.getUserKazaplanServiceProducts(
            ['product'],
            {
              query: new HttpParams({
                fromObject: {
                  'userKazaplanService.reachfiveId': user.reach5Id,
                  'product.premium': true,
                  'order[endSubscriptionDate]': 'DESC',
                  'order[rescissionSubscriptionDate]': 'DESC',
                  // 'itemsPerPage': 1,
                },
              }),
              refresh: true,
            },
          ),
        ),
        map((userKazaplanServiceProducts: HydraCollection<UserKazaplanServiceProduct>) => !userKazaplanServiceProducts ? null : userKazaplanServiceProducts['hydra:member'][0] ?? undefined),
      );
  }

  lookForPurchasedProduct(product: Product): Observable<UserKazaplanServiceProduct> {
    return this.userStoreService.userStore
      .pipe(
        catchError((e) => {
          console.log('lookForPurchasedProduct / catchError', e);

          return throwError(() => e);
        }),
        filterNullOrUndefined(),
        distinctObject(),
        take(1),
        switchMap((user: User) => this.userKazaplanServiceProductService.getUserKazaplanServiceProducts(
            ['product'],
            {
              query: new HttpParams({
                fromObject: {
                  'userKazaplanService.reachfiveId': user.reach5Id,
                  'product': product['@id'],
                  'order[createdAt]': 'DESC',
                  'exists[rescissionSubscriptionDate]': false,
                  'itemsPerPage': 1,
                },
              }),
              refresh: true,
            },
          ),
        ),
        map((userKazaplanServiceProducts: HydraCollection<UserKazaplanServiceProduct>) => userKazaplanServiceProducts['hydra:member'].shift()),
        map((userKazaplanServiceProduct: UserKazaplanServiceProduct) => userKazaplanServiceProduct && new Date(userKazaplanServiceProduct.createdAt).getTime() > (Date.now() - (10 * 60 * 1000)) ? userKazaplanServiceProduct : null)
      );
  }

  getProduct(shortName: string): Observable<Product> {
    if (this.productsCache.hasOwnProperty(shortName)) {
      return of(this.productsCache[shortName]);
    }

    return this.isProductsLoading
      .pipe(
        filter((isProductsLoading) => !isProductsLoading),
        switchMap(() => {
          if (this.products) {
            return of(this.products);
          }

          this.isProductsLoading.next(true);

          return this.productService.getProducts(null, {
            query: new HttpParams({
              fromObject: {
                display: true,
              },
            }),
          }).pipe(
            catchError((e) => {
              console.log('ProductService.getProducts / catchError', e);

              return throwError(() => e);
            }),
            retry(3),
            map((productCollection: HydraCollection<Product>) => productCollection['hydra:member']),
            tap((products: Array<Product>) => this.products = products),
            tap(() => this.isProductsLoading.next(false)),
          );
        }),
        map((products: Array<Product>) => products.filter((product) => product.shortName === shortName).shift()),
        tap((product: Product) => {
          if (!!product) {
            this.productsCache[product.shortName] = product;
          }
        }),
      );
  }
}
